import FormBuilderContainer from '../FormBuilder/FormBuilderContainer';
import SequentialFormContainer from '../SequentialForm/SequentialFormContainer';

// Group icons
import GradeIcon from '@mui/icons-material/Grade';
import CodeIcon from '@mui/icons-material/Code';
import WebhookIcon from '@mui/icons-material/Webhook';
import StorageIcon from '@mui/icons-material/Storage';
import AlarmIcon from '@mui/icons-material/Alarm';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TableChartIcon from '@mui/icons-material/TableChart';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HistoryIcon from '@mui/icons-material/History';
import AIGroupIcon from './AIGroupIcon';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

export const ComponentGroups = [
  {
    title: 'Favourites',
    description: 'Most popular and common components',
    icon: <GradeIcon sx={{ color: '#f9a825' }} />,
    components: ['sequential_form', 'branch_paths', 'email', 'form', 'session_variables'],
  },
  {
    title: 'AI',
    description: 'AI to perform actions and tasks',
    icon: <AutoAwesomeIcon sx={{ color: '#944BA8' }} />,
    components: ['ai_assistant', 'ai_agent'],
  },
  {
    title: 'API',
    description: 'Receive webhooks and make API requests',
    icon: <WebhookIcon sx={{ color: '#0288d1' }} />,
    components: [
      'webhook_trigger',
      'http_request',
      'api_request_and_response',
      'trigger_from_api_polling',
      'await_external_event',
    ],
  },
  {
    title: 'Code',
    description: 'Write and run code',
    icon: <CodeIcon sx={{ color: '#388e3c' }} />,
    components: ['code', 'custom_function'],
  },
  {
    title: 'Tables',
    description: 'Create, update and query a Workflow86 table',
    icon: <StorageIcon sx={{ color: '#d32f2f' }} />,
    components: [
      'create_database_record',
      'query_database_record',
      'update_database_record',
      'trigger_on_database_update',
      'create_row_v2',
      'query_table_v2',
      'update_row_v2',
      'trigger_table_v2',
    ],
  },
  {
    title: 'Date and Time',
    description: 'Trigger workflows at a particular date and time, delay by time',
    icon: <AlarmIcon sx={{ color: '#0097a7' }} />,
    components: ['schedule_start', 'time_delay'],
  },
  {
    title: 'Documents',
    description: 'Generate documents and PDFs',
    icon: <DescriptionIcon sx={{ color: '#5c6bc0' }} />,
    components: ['document_editor', 'create_file'],
  },
  {
    title: 'Email',
    description: 'Trigger from email, send emails',
    icon: <EmailOutlinedIcon sx={{ color: '#7cb342' }} />,
    components: ['email', 'trigger_via_email'],
  },
  {
    title: 'Forms and Tasks',
    description: 'Create forms and tasks to be completed by users',
    icon: <CheckBoxIcon sx={{ color: '#0277bd' }} />,
    components: ['form', 'sequential_form', 'form_section', 'dynamic_form', 'dynamic_review_task', 'form_step', 'artifact'],
  },
  {
    title: 'Logic',
    description: 'Create logic if-this-then-that statements',
    icon: <DeviceHubIcon sx={{ color: '#8e24aa' }} />,
    components: ['conditional_logic', 'gateway'],
  },
  {
    title: 'Paths and loops',
    description: 'Control the path taken when a workflow runs, run loops',
    icon: <AccountTreeIcon sx={{ color: '#f57c00' }} />,
    components: [
      'branch_paths',
      'gateway',
      'loop_through_list',
      'merge_workflow',
      'converge_threads',
    ],
  },
  {
    title: 'Spreadsheets',
    description: 'Trigger workflows from spreadsheets, create spreadsheets',
    icon: <TableChartIcon sx={{ color: '#00897b' }} />,
    components: ['bulk_assessment'],
  },
  {
    title: 'Tools and utilities',
    description: 'Tools to transform and manipulate data, perform calculations and more',
    icon: <HomeRepairServiceIcon sx={{ color: '#5d4037' }} />,
    components: ['calculator', 'list_transform', 'session_variables', 'parse_json'],
  },
  {
    title: 'Integrations',
    description: 'Pre-built integrations with external apps and tools',
    icon: <ExitToAppIcon sx={{ color: '#558b2f' }} />,
    components: ['trigger_from_zapier', 'trigger_a_zap', 'slack'],
  },
  {
    title: 'Legacy',
    description: 'Older components that have been replaced by newer versions',
    icon: <HistoryIcon sx={{ color: '#9e9e9e' }} />,
    components: [
      'knowledge_bank',
      'api_export',
      'calculator',
      'conditional_logic',
      'parse_json',
      'list_transform',
      'conditional_workflow_path',
      'trigger_from_zapier',
      'trigger_a_zap',
      'trigger_from_external_app',
      'trigger_from_a_workflow',
      'trigger_another_workflow',
      'ai_assistant',
      'api_request_and_response',
      'slack',
      'session_variables',
      'webhook',
      'conditional_workflow',
    ],
    isLegacy: true,
  },
];

export const ComponentRegistry = [
  {
    name: 'ai_assistant',
    displayName: 'AI Request',
    description: 'Use AI to perform a task or action and return an output',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/ai_assistant.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#944BA8',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    hidden: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'ai_agent',
    displayName: 'AI Agent',
    description:
      'Invoke a configured AI agent with tools to perform multi-step reasoning and actions',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/ai_agent.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#944BA8',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    hidden: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'api_export',
    displayName: 'API Export',
    description: 'Make a HTTP POST to an API endpoint',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/api_export.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#788ac8',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'http_request',
    displayName: 'HTTP Request',
    description: 'Make HTTP requests to external APIs with support for all methods, headers, auth, and credentials',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/http_request.svg',
    stateless: false,
    interactive: true,
    baseColor: '#2a4cc2',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'api_request_and_response',
    displayName: 'API Request and Response',
    description: 'Make an API request and parse the data from the response',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/api_request_and_response.svg',
    stateless: false,
    interactive: true,
    baseColor: '#2a4cc2',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: true,
  },
  {
    name: 'sequential_form',
    displayName: 'Assign Task',
    description:
      'Assign someone a task to complete and run the next component when that task is completed',
    // discoveryEndpoint: "component-service/sequential_form/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/sequential_form.svg',
    sidebarLogo: '/svgs/sequential_form.svg',
    stateless: false,
    interactive: true,
    action: CanvasNodeWidget => {
      SequentialFormContainer.diagramEngine = CanvasNodeWidget.props.diagramEngine;
      SequentialFormContainer.node = CanvasNodeWidget.props.node;
      CanvasNodeWidget.props.navigate(
        window.location.pathname +
          `/sequential_form/${CanvasNodeWidget.props.node.extras.component_id || ''}`
      );
    },
    tier: 'FREE',
    baseColor: '#2670ff',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'branch_paths',
    displayName: 'Branch Paths',
    description: 'Branch workflow into Conditional Paths',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/branch_paths.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#000000',
    isTrigger: false,
    hasAccessControl: false,
    isAlwaysPublic: true,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'calculator',
    displayName: 'Calculator',
    description: 'Perform a calculation',
    // discoveryEndpoint: "component-service/calculator/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/calculator.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#ff7e7e',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'code',
    displayName: 'Run Code',
    description: 'Run custom Python or Javascript code',
    // discoveryEndpoint: "component-service/code/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/code.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#000000',
    isTrigger: false,
    hasAccessControl: false,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'conditional_logic',
    displayName: 'Conditional Logic',
    description: 'Run a logic statement to set the value of a variable/placeholder',
    // discoveryEndpoint: "component-service/conditional-logic/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/conditional_logic.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#9cc5c5',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'conditional_workflow',
    displayName: 'Conditional Path',
    description:
      'Control whether the workflow continues to the next action or stops based on a logic statement',
    // discoveryEndpoint: "component-service/conditional-workflow/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/conditional_workflow.svg',
    stateless: false,
    interactive: true,
    fromWorkflowPath: false,
    tier: 'FREE',
    baseColor: '#FF7E3E',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'conditional_workflow_path',
    displayName: 'Conditional Workflow Paths',
    description: 'Create paths that run based on a conditional statement',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/conditional_workflow_path.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#868686',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
  },

  {
    name: 'create_database_record',
    displayName: 'Create Table Record',
    description: 'Create a new row/record in a table to store information or data',
    // discoveryEndpoint: "component-service/create_database_record/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/create_database_record.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#E10050',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'knowledge_bank',
    displayName: 'Content Library',
    description: 'Store content as variables',
    // discoveryEndpoint: "component-service/knowledge-bank/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/knowledge.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#a880e8',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'document_editor',
    displayName: 'Document Editor',
    description: 'Generate word or PDF documents from a template',
    // discoveryEndpoint: "component-service/document-editor/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/report.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#ed6696',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'create_file',
    displayName: 'Create File',
    description: 'Generate files (CSV, JSON, PDF, XLSX, TXT, MD, DOCX) from templates and data',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/create_file.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#2e7d32',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'email',
    displayName: 'Email',
    description: 'Send an email',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/email.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#ffb74d',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'form',
    displayName: 'Form',
    description: 'Online form which triggers a workflow when submitted',
    // discoveryEndpoint: "component-service/form/placeholder",
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/form.svg',
    sidebarLogo: '/svgs/form.svg',
    stateless: false,
    interactive: true,
    action: CanvasNodeWidget => {
      FormBuilderContainer.diagramEngine = CanvasNodeWidget.props.diagramEngine;
      FormBuilderContainer.node = CanvasNodeWidget.props.node;
      CanvasNodeWidget.props.navigate(
        window.location.pathname +
          `/formbuilder/${CanvasNodeWidget.props.node.extras.component_id || ''}`
      );
    },
    tier: 'FREE',
    baseColor: '#7ac0f8',
    isTrigger: true,
    hasAccessControl: true,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'form_section',
    displayName: 'Form Section',
    description:
      'Redirect the user to another form section when a previous form or task has been submitted',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/form.svg',
    sidebarLogo: '/svgs/form.svg',
    stateless: false,
    interactive: true,
    action: CanvasNodeWidget => {
      SequentialFormContainer.diagramEngine = CanvasNodeWidget.props.diagramEngine;
      SequentialFormContainer.node = CanvasNodeWidget.props.node;
      CanvasNodeWidget.props.navigate(
        window.location.pathname +
          `/form_section/${CanvasNodeWidget.props.node.extras.component_id || ''}`
      );
    },
    tier: 'FREE',
    baseColor: '#7ac0f8',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'list_transform',
    displayName: 'List Transform',
    description: 'Transform and manipulate list type data',
    // discoveryEndpoint: "component-service/list-transform/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/list_transform.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#48a80f',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: true,
  },
  {
    name: 'loop_through_list',
    displayName: 'Split into Threads',
    description:
      'Split a list into individual values and start a parallel run of all further components for each individual value',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/loop_through_list.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#48A80F',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'merge_workflow',
    displayName: 'Merge Paths',
    description:
      'Merge multiple workflow paths together and trigger a component when one or more connections have been completed',
    // discoveryEndpoint: "component-service/workflow_data/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/merge_workflow.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#ff3d00',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'converge_threads',
    displayName: 'Merge Threads',
    description: 'Wait for all thread runs to complete, then continue once with merged output',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/converge_threads.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#48A80F',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'parse_json',
    displayName: 'Parse JSON',
    description: 'Parse a JSON into placeholder values',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/parse_json.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#001356',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: true,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'query_database_record',
    displayName: 'Query Table',
    description:
      'Query a table and return all values in a column or return values from a column based on a conditional statement',
    // discoveryEndpoint: "component-service/query_database_record/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/query_database.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#E10050',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'schedule_start',
    displayName: 'Schedule Start',
    description: 'Start this workflow a certain time or date',
    // discoveryEndpoint: "component-service/schedule-start/placeholder",
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/schedule_start.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#1D7573',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'slack',
    displayName: 'Send Slack Message',
    description: 'Send a Slack message',
    // discoveryEndpoint: "component-service/slack/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/slack.svg',
    stateless: false,
    interactive: true,
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'session_variables',
    displayName: 'Session Variables',
    description: 'Create variables/placeholders that can be used in the rest of the workflow',
    // discoveryEndpoint: "component-service/code/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/session_variables.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#000000',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: false,
  },

  {
    name: 'time_delay',
    displayName: 'Time Delay',
    description: 'Delay the next action until a certain time or date',
    // discoveryEndpoint: "component-service/time-delay/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/time_delay.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#67C9D3',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_a_zap',
    displayName: 'Trigger a Zap',
    description: 'Trigger a zap in Zapier',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/zapier-icon.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#FF4A00',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_from_zapier',
    displayName: 'Trigger from Zapier',
    description: 'Trigger a workflow from a zap in Zapier',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/zapier-icon.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#FF4A00',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_from_external_app',
    displayName: 'Trigger from External App',
    description: 'Trigger a workflow from an External App',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/trigger_from_external_app.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#5585A7',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_another_workflow',
    displayName: 'Trigger another Workflow',
    description: 'Trigger a separate workflow or create a loop',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/trigger_another_workflow.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#868686',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_from_a_workflow',
    displayName: 'Trigger from a Workflow',
    description: 'Trigger this workflow when another workflow is run or create a loop',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/trigger_from_a_workflow.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#2C2C2C',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_via_email',
    displayName: 'Trigger via Email',
    description: 'Trigger this workflow when an email is received at a specific email address',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/trigger_via_email.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#FFB800',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'update_database_record',
    displayName: 'Update Table Record',
    description: 'Update the values in a row/record in a table',
    // discoveryEndpoint: "component-service/update_database_record/placeholder",
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/update_database_record.svg',
    stateless: false,
    interactive: true,
    baseColor: '#E10050',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_on_database_update',
    displayName: 'Trigger on Table Update',
    description: 'Trigger this workflow when a record in a table is updated',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/trigger_on_database_update.svg',
    stateless: false,
    interactive: true,
    baseColor: '#E10050',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'query_table_v2',
    displayName: 'Query Table',
    description: 'Query a table with full SQL-like capabilities: nested conditions, aggregation, sorting, and column projection',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/query_database.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#E10050',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'create_row_v2',
    displayName: 'Create Row',
    description: 'Create a new row in a table with typed column values',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/create_database_record.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#E10050',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'update_row_v2',
    displayName: 'Update Row',
    description: 'Update an existing row in a table by row ID',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/update_database_record.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#E10050',
    isTrigger: false,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'trigger_table_v2',
    displayName: 'Table Trigger',
    description: 'Trigger a workflow when a row is inserted, updated, or deleted in a table',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/trigger_on_database_update.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#E10050',
    isTrigger: true,
    hasAccessControl: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'bulk_assessment',
    displayName: 'Upload Spreadsheet',
    description: 'Upload a spreadsheet and trigger a workflow session for each row',
    // discoveryEndpoint: "component-service/bulk_assessment/placeholder",
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/bulk_assessment.svg',
    sidebarLogo: '/svgs/bulk_assessment.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#5bc4b0',
    isTrigger: true,
    hasAccessControl: true,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'webhook_trigger',
    displayName: 'Webhook',
    description: 'Trigger a workflow via webhook',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/webhook_trigger.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#42A5F5',
    isTrigger: true,
    hasAccessControl: true,
    isAlwaysPublic: true,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'webhook',
    displayName: 'Webhook Import (Legacy)',
    description: 'Trigger a workflow when a webhook is received',
    // discoveryEndpoint: "component-service/webhook/placeholder",
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/webhook.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#8add9e',
    isTrigger: true,
    hasAccessControl: true,
    isAlwaysPublic: true,
    isExcluded: true,
    isFloatingIcon: false,
  },
  {
    name: 'trigger_from_api_polling',
    displayName: 'Trigger from API Polling',
    description: 'Trigger a workflow by polling an API at regular intervals',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/trigger_from_api_polling.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#0288D1',
    isTrigger: true,
    hasAccessControl: false,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'await_external_event',
    displayName: 'Await External Event',
    description: 'Pause and wait for an external event (webhook or API polling) before continuing',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/await_external_event.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#00897B',
    isTrigger: false,
    hasAccessControl: false,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'UI.dbIcon',
    displayName: 'Table',
    description: 'A Workflow86 table',
    logo: '/svgs/database.svg',
    stateless: false,
    interactive: false,
    tier: 'FREE',
    baseColor: '#1C1B1F',
    hasAccessControl: true,
    isAlwaysPublic: true,
    newTabUrl: '/databases/database_management',
    hidden: true,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'dynamic_form',
    displayName: 'Dynamic Form',
    description: 'Create a dynamic form that triggers a workflow when submitted',
    hasInput: false,
    hasOutput: true,
    logo: '/svgs/form.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#2670ff',
    isTrigger: true,
    hasAccessControl: true,
    isAlwaysPublic: false,
    hidden: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'dynamic_review_task',
    displayName: 'Create Task',
    description: 'Assign a dynamic form to a user and pause until they submit',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/sequential_form.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#2670ff',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    hidden: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'form_step',
    displayName: 'Form Step',
    description: 'Self-service mid-workflow form step. Can start or attach to a form flow.',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/form.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#2670ff',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    hidden: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'artifact',
    displayName: 'Artifact',
    description: 'Create a human-facing artifact and wait for a submitted event',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/artifact.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#2670ff',
    isTrigger: false,
    hasAccessControl: true,
    isAlwaysPublic: false,
    hidden: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
  {
    name: 'gateway',
    displayName: 'Gateway',
    description: 'Route workflow based on conditions or fan out to all paths',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/gateway.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#F5A623',
    isTrigger: false,
    hasAccessControl: false,
    isAlwaysPublic: true,
    isExcluded: false,
    isFloatingIcon: false,
  },
  {
    name: 'custom_function',
    displayName: 'Custom Function',
    description:
      'Run a tenant-defined custom function. Pick the function in the drawer; inputs and outputs derive from its schemas.',
    hasInput: true,
    hasOutput: true,
    logo: '/svgs/custom_function.svg',
    stateless: false,
    interactive: true,
    tier: 'FREE',
    baseColor: '#944BA8',
    isTrigger: false,
    hasAccessControl: false,
    isAlwaysPublic: false,
    isExcluded: false,
    isFloatingIcon: true,
  },
];

export default ComponentRegistry;

function createFactory(ComponentRegistry) {
  const node_factory = {};
  ComponentRegistry.forEach(registered_node => {
    node_factory[registered_node.name] = {
      hidden: registered_node.hidden,
      name: registered_node.name,
      logo: registered_node.logo,
      sidebarLogo: registered_node.sidebarLogo,
      description: registered_node.description,
      isInteractive: registered_node.interactive,
      displayName: registered_node.displayName,
      tier: registered_node.tier,
      baseColor: registered_node.baseColor,
      isTrigger: registered_node.isTrigger,
      hasAccessControl: registered_node.hasAccessControl,
      isAlwaysPublic: registered_node.isAlwaysPublic,
      hasOutput: registered_node.hasOutput,
      hasInput: registered_node.hasInput,
      newTabUrl: registered_node.newTabUrl,
      fromWorkflowPath: registered_node.fromWorkflowPath,
      isExcluded: registered_node.isExcluded,
      isFloatingIcon: registered_node.isFloatingIcon,
    };
  });
  return node_factory;
}

export const ComponentFactory = createFactory(ComponentRegistry);
